import React, { useEffect, useState } from "react";
import { api, UNIT_META } from "@/lib/api";
import { PageHeader, Panel, EmptyState } from "@/components/ui-parts";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DialogShell, Field, Input, Textarea, UnitSelect } from "@/components/form-helpers";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const todayISO = () => new Date().toISOString().slice(0, 10);
const CATEGORIES = [
  { key: "raw", label: "Raw Material" },
  { key: "finished", label: "Finished Goods" },
  { key: "packing", label: "Packing Material" },
  { key: "spare", label: "Spare Parts" },
];

export default function Inventory() {
  const [rows, setRows] = useState([]);
  const [category, setCategory] = useState("raw");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    unit: "", date: todayISO(), item: "", category: "raw", opening_stock_kg: "", received_kg: "", dispatched_kg: "", closing_stock_kg: "", rate: "", remarks: "",
  });

  const load = async () => {
    const r = await api.get("/inventory", { params: { category } });
    setRows(r.data);
  };
  useEffect(() => { load(); }, [category]);

  const submit = async () => {
    try {
      await api.post("/inventory", {
        ...form,
        category,
        opening_stock_kg: Number(form.opening_stock_kg) || 0,
        received_kg: Number(form.received_kg) || 0,
        dispatched_kg: Number(form.dispatched_kg) || 0,
        closing_stock_kg: Number(form.closing_stock_kg) || 0,
        rate: Number(form.rate) || 0,
      });
      toast.success("Inventory saved");
      setOpen(false);
      setForm({ ...form, item: "", opening_stock_kg: "", received_kg: "", dispatched_kg: "", closing_stock_kg: "", rate: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Data Entry"
        title="Inventory"
        description="Raw material, finished goods, packing material and spare parts stock movement."
        testid="inventory-header"
        actions={
          <Button data-testid="add-inventory-btn" onClick={() => { setForm({ ...form, category }); setOpen(true); }} className="bg-amber-400 hover:bg-amber-300 text-black rounded-none">
            <Plus className="mr-2" size={14} weight="bold" /> New Entry
          </Button>
        }
      />

      <Tabs value={category} onValueChange={setCategory} className="mb-4">
        <TabsList className="rounded-none bg-[#0e0e11] border border-zinc-800 h-auto p-0" data-testid="inventory-tabs">
          {CATEGORIES.map((c) => (
            <TabsTrigger
              key={c.key}
              value={c.key}
              data-testid={`inv-tab-${c.key}`}
              className="rounded-none data-[state=active]:bg-amber-400 data-[state=active]:text-black px-6 py-2 label-caps text-[10px]"
            >
              {c.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Panel title={`${CATEGORIES.find((c) => c.key === category)?.label} (${rows.length})`}>
        {rows.length === 0 ? (
          <EmptyState message="No inventory records" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="label-caps">Date</TableHead>
                <TableHead className="label-caps">Unit</TableHead>
                <TableHead className="label-caps">Item</TableHead>
                <TableHead className="label-caps text-right">Opening</TableHead>
                <TableHead className="label-caps text-right">Received</TableHead>
                <TableHead className="label-caps text-right">Dispatched</TableHead>
                <TableHead className="label-caps text-right">Closing</TableHead>
                <TableHead className="label-caps text-right">Rate</TableHead>
                <TableHead className="label-caps text-right">Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="border-zinc-800">
                  <TableCell className="font-mono text-xs">{r.date}</TableCell>
                  <TableCell><span className="label-caps" style={{ color: UNIT_META[r.unit]?.accent }}>{UNIT_META[r.unit]?.tag}</span></TableCell>
                  <TableCell>{r.item}</TableCell>
                  <TableCell className="text-right font-mono">{r.opening_stock_kg}</TableCell>
                  <TableCell className="text-right font-mono text-emerald-400">{r.received_kg}</TableCell>
                  <TableCell className="text-right font-mono text-red-400">{r.dispatched_kg}</TableCell>
                  <TableCell className="text-right font-mono text-amber-400">{r.closing_stock_kg}</TableCell>
                  <TableCell className="text-right font-mono">{r.rate}</TableCell>
                  <TableCell className="text-right font-mono">{(r.closing_stock_kg * r.rate).toFixed(0)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      <DialogShell open={open} onOpenChange={setOpen} title="Inventory Entry" onSubmit={submit} submitLabel="Save" testidSubmit="save-inventory-btn">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Unit"><UnitSelect testid="inv-unit" value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} /></Field>
          <Field label="Category">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="rounded-none bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Date"><Input data-testid="inv-date" type="date" className="rounded-none bg-zinc-950 border-zinc-800" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
          <Field label="Item name"><Input data-testid="inv-item" className="rounded-none bg-zinc-950 border-zinc-800" value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} placeholder="e.g. Wheat, Chana Daal, PP Bags…" /></Field>
          <Field label="Opening Stock (kg)"><Input data-testid="inv-opening" type="number" step="0.01" className="rounded-none bg-zinc-950 border-zinc-800" value={form.opening_stock_kg} onChange={(e) => setForm({ ...form, opening_stock_kg: e.target.value })} /></Field>
          <Field label="Received (kg)"><Input data-testid="inv-received" type="number" step="0.01" className="rounded-none bg-zinc-950 border-zinc-800" value={form.received_kg} onChange={(e) => setForm({ ...form, received_kg: e.target.value })} /></Field>
          <Field label="Dispatched (kg)"><Input data-testid="inv-dispatched" type="number" step="0.01" className="rounded-none bg-zinc-950 border-zinc-800" value={form.dispatched_kg} onChange={(e) => setForm({ ...form, dispatched_kg: e.target.value })} /></Field>
          <Field label="Closing Stock (kg)"><Input data-testid="inv-closing" type="number" step="0.01" className="rounded-none bg-zinc-950 border-zinc-800" value={form.closing_stock_kg} onChange={(e) => setForm({ ...form, closing_stock_kg: e.target.value })} /></Field>
          <Field label="Rate ₹"><Input data-testid="inv-rate" type="number" step="0.01" className="rounded-none bg-zinc-950 border-zinc-800" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} /></Field>
        </div>
        <Field label="Remarks"><Textarea className="rounded-none bg-zinc-950 border-zinc-800" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
      </DialogShell>
    </div>
  );
}
